/**
 * Hidden structures: dungeons, mineshafts, buried treasure and fossils.
 */

import { CHUNK_SIZE, SEA_LEVEL } from '../../constants';
import { AIR, LAVA_ID, RAIL_ID, TORCH_ID, WATER_ID } from '../../blocks';
import { mulberry32 } from '../../noise';
import { Biome } from '../biomes';
import { type GenCtx, getBlock, setBlock, vy } from '../ctx';
import type { TerrainGenerator } from '../terrain';
import {
  block, type Builder, cell, chance, F, int, centredPiece, type Piece, type Rng, seedOf, type StructureType, worldPiece,
} from './kit';

const COBBLE = block('Cobblestone');
const MOSSY = block('Mossy Cobblestone');

// --- dungeon ---------------------------------------------------------------------

/** Vanilla MonsterRoomFeature: a cobblestone room (mossy floor), the spawner in
 *  the middle — a mossy block stands in — and up to two chests on the walls. */
function buildDungeon(b: Builder, w: number, d: number): void {
  const y = b.y;
  b.room(0, y, 0, w - 1, y + 5, d - 1, cell.mix([[COBBLE, 3], [MOSSY, 1]]));
  b.fill(0, y, 0, w - 1, y, d - 1, cell.mix([[COBBLE, 1], [MOSSY, 1]]));
  b.set(w >> 1, y + 1, d >> 1, MOSSY);
  b.chest(1, y + 1, 1 + int(b.rnd, d - 2), F.PU);
  if (chance(b.rnd, 0.6)) b.chest(w - 2, y + 1, 1 + int(b.rnd, d - 2), F.NU);
}

function dungeon(g: TerrainGenerator, x: number, z: number, rnd: Rng): Piece[] | null {
  const c = g.columnClimate(x, z);
  const top = Math.min(c.height, SEA_LEVEL + 10) - 16;
  if (top < 12) return null;
  const y = 8 + int(rnd, top - 8);
  const w = 7 + 2 * int(rnd, 2), d = 7 + 2 * int(rnd, 2);
  return [centredPiece(x, z, 0, w, d, y, false, seedOf(rnd), (b) => buildDungeon(b, w, d))];
}

// --- buried treasure ----------------------------------------------------------------

function buriedTreasure(g: TerrainGenerator, x: number, z: number, rnd: Rng): Piece[] | null {
  const c = g.columnClimate(x, z);
  if (c.biome !== Biome.Beach && c.biome !== Biome.SnowyBeach) return null;
  return [centredPiece(x, z, 0, 1, 1, c.height, false, seedOf(rnd), (b) => b.chest(0, b.y - 2, 0, F.PV))];
}

// --- fossil ----------------------------------------------------------------------

const BONE = block('Bone Block');

/**
 * A fossil (vanilla fossil/spine_* and skull_*): a bone-block spine with
 * arched ribs, or a skull, buried in desert or swamp ground. Like vanilla, a
 * share of the bones has turned to coal.
 */
function buildFossil(b: Builder, len: number, skull: boolean): void {
  const y = b.y, r = b.rnd;
  const bone = (u: number, yy: number, v: number): void => {
    if (r() < 0.1) return; // vanilla drops ~10% of the template's blocks
    b.set(u, yy, v, r() < 0.15 ? block('Coal Ore') : BONE);
  };
  if (skull) {
    for (let u = 0; u < 5; u++) for (let v = 0; v < 5; v++) for (let dy = 0; dy < 4; dy++) {
      const shell = u === 0 || u === 4 || v === 0 || v === 4 || dy === 0 || dy === 3;
      const eye = dy === 2 && v === 0 && (u === 1 || u === 3);
      if (shell && !eye) bone(u, y + dy, v);
    }
    return;
  }
  for (let v = 0; v < len; v++) {
    bone(2, y + 3, v); // the spine
    if (v % 2 === 1 && v > 0 && v < len - 1) {
      // a rib arching down either side
      for (const u of [0, 4]) { bone(u, y + 1, v); bone(u, y + 2, v); }
      bone(1, y + 3, v); bone(3, y + 3, v);
    }
  }
}

function fossil(g: TerrainGenerator, x: number, z: number, rnd: Rng): Piece[] | null {
  const c = g.columnClimate(x, z);
  if (c.biome !== Biome.Desert && c.biome !== Biome.Swamp && c.biome !== Biome.MangroveSwamp) return null;
  const y = c.height - 9 - int(rnd, 8);
  if (y < 8) return null;
  const skull = chance(rnd, 0.35);
  const len = skull ? 5 : 9 + 2 * int(rnd, 3);
  return [centredPiece(x, z, int(rnd, 4), 5, len, y, false, seedOf(rnd), (b) => buildFossil(b, len, skull))];
}

// --- mineshaft -------------------------------------------------------------------

const PLANKS = block('Oak Planks');
const POST = block('Oak Log');
const FENCE = block('Oak Fence');
const COBWEB = block('Cobweb');

/** One straight corridor: a 3 x 3 bore with plank-and-post supports every 5
 *  blocks, rails, cobwebs, the odd torch and chest (vanilla puts it in a
 *  minecart), bridging caves and lava lakes with planks. */
function corridor(x: number, z: number, dir: number, len: number, y: number, seed: number): Piece {
  const dx = [1, -1, 0, 0][dir], dz = [0, 0, 1, -1][dir];
  const ex = x + dx * (len - 1), ez = z + dz * (len - 1);
  const across = dx !== 0 ? 1 : 0; // the bore's width runs along z when the corridor runs along x
  return worldPiece(
    Math.min(x, ex) - (1 - across), Math.min(z, ez) - across, Math.max(x, ex) + (1 - across), Math.max(z, ez) + across, y,
    (ctx: GenCtx) => {
      const r = mulberry32(seed);
      const chestAt = r() < 0.3 ? int(r, len) : -1;
      const sx = dx !== 0 ? 0 : 1, sz = dz !== 0 ? 0 : 1; // sideways step
      for (let i = 0; i < len; i++) {
        const cx = x + dx * i, cz = z + dz * i;
        const web = r(), rail = r();
        const support = i % 5 === 2;
        for (let o = -1; o <= 1; o++) {
          const bx = cx + sx * o, bz = cz + sz * o;
          for (let dy = 1; dy <= 3; dy++) setBlock(ctx, bx, y + dy, bz, AIR);
          const below = getBlock(ctx, bx, y, bz);
          if (below === AIR || below === WATER_ID || below === LAVA_ID) {
            setBlock(ctx, bx, y, bz, PLANKS);
            if (o !== 0 && !support) setBlock(ctx, bx, y + 1, bz, FENCE); // railing on a bridge, as vanilla
          }
          if (support) {
            if (o !== 0) { setBlock(ctx, bx, y + 1, bz, POST); setBlock(ctx, bx, y + 2, bz, POST); }
            setBlock(ctx, bx, y + 3, bz, PLANKS);
          }
        }
        if (support && web < 0.5) setBlock(ctx, cx, y + 2, cz, COBWEB);
        else if (i % 5 === 3 && web < 0.15) setBlock(ctx, cx + sx, y + 3, cz + sz, COBWEB);
        if (i === chestAt && !support) setBlock(ctx, cx + sx, y + 1, cz + sz, block('Chest'));
        if (rail < 0.7 && !support) setBlock(ctx, cx, y + 1, cz, RAIL_ID);
        if (i % 10 === 7 && web > 0.6) setBlock(ctx, cx - sx, y + 1, cz - sz, TORCH_ID);
      }
    },
  );
}

/** Vanilla MineshaftPieces: a dirt-floored central room, corridors branching
 *  off it and off each other's ends, all on one level. */
function mineshaft(g: TerrainGenerator, x: number, z: number, rnd: Rng): Piece[] | null {
  const c = g.columnClimate(x, z);
  if (c.height < SEA_LEVEL - 6) return null;
  const y = Math.min(c.height - 14, vy(-20) + int(rnd, vy(40) - vy(-20)));
  if (y < 8) return null;
  const pieces: Piece[] = [centredPiece(x, z, 0, 9, 9, y, false, seedOf(rnd), (b) => {
    b.fill(0, b.y + 1, 0, 8, b.y + 5, 8, AIR);
    b.fill(0, b.y, 0, 8, b.y, 8, block('Dirt'));
  })];
  const reach = 5 * CHUNK_SIZE;
  const grow = (px: number, pz: number, dir: number, depth: number): void => {
    if (depth > 7 || pieces.length > 70) return;
    const len = (2 + int(rnd, 3)) * 5;
    const dx = [1, -1, 0, 0][dir], dz = [0, 0, 1, -1][dir];
    const ex = px + dx * len, ez = pz + dz * len;
    if (Math.abs(ex - x) > reach || Math.abs(ez - z) > reach) return;
    pieces.push(corridor(px, pz, dir, len, y, seedOf(rnd)));
    // a crossing: carry straight on and/or branch to either side
    if (chance(rnd, 0.65)) grow(ex, ez, dir, depth + 1);
    for (const t of dir < 2 ? [2, 3] : [0, 1]) if (chance(rnd, 0.4)) grow(ex, ez, t, depth + 1);
  };
  for (let dir = 0; dir < 4; dir++) {
    if (chance(rnd, 0.85)) grow(x + [5, -5, 0, 0][dir], z + [0, 0, 5, -5][dir], dir, 0);
  }
  return pieces;
}

export const MINESHAFT: StructureType = {
  name: 'Mineshaft', spacing: 16, separation: 4, salt: 0x3157, reach: 6, generate: mineshaft,
};
export const FOSSIL: StructureType = {
  name: 'Fossil', spacing: 12, separation: 3, salt: 0xf055, reach: 1, hud: false, generate: fossil,
};
export const BURIED_TREASURE: StructureType = {
  name: 'Buried Treasure', spacing: 10, separation: 2, salt: 0x7b00, reach: 1, hud: false, generate: buriedTreasure,
};
export const DUNGEON: StructureType = {
  name: 'Dungeon', spacing: 5, separation: 1, salt: 0xd0d0, reach: 1, hud: false, generate: dungeon,
};
